import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import { Config } from '../config'
import { PricingType } from '../types/pricing'

interface UsePriceTestArgs {
  planPrice?: string
  priceExperiment?: string
  hasFreeTrial?: boolean
  basePrice?: number
}

const safeParseJson = (
  pricingJson: string,
  hasFreeTrial: boolean,
  planPrice: string,
  priceExperiment: string,
  basePrice: number
): PricingType => {
  try {
    const data = (typeof pricingJson === 'string'
      ? JSON.parse(pricingJson as string)
      : pricingJson) as PricingType
    const freeTrial = data?.freeTrial ?? hasFreeTrial
    const price = data?.price ?? planPrice
    const experiment = data?.pricingExperiment ?? priceExperiment
    const updatedBasePrice = data?.basePrice ?? basePrice
    return {
      freeTrial,
      price,
      pricingExperiment: experiment,
      basePrice: updatedBasePrice,
    }
  } catch (error) {
    return {
      freeTrial: hasFreeTrial,
      price: planPrice,
      pricingExperiment: priceExperiment,
      basePrice,
    }
  }
}

const usePriceTest = ({
  planPrice = '$9.95',
  priceExperiment = 'BASE',
  hasFreeTrial = true,
  basePrice = 9.95,
}: UsePriceTestArgs = {}) => {
  // Only one feature test will be enabled at a time (either user will come from SEM or non SEM)
  const {
    variables: pricingVariables,
    clientReady: pricingClientReady,
    isEnabled: pricingTestEnabled,
  } = useSafeOptimizelyFeature(Config.pricingTestFeatureName)
  const {
    variables: semPricingVariables,
    clientReady: semPricingClientReady,
    isEnabled: semPricingTestEnabled,
  } = useSafeOptimizelyFeature(Config.semPricingTestFeatureName)
  const pricingExperimentReady = pricingClientReady && semPricingClientReady
  const pricingExperimentEnabled = pricingTestEnabled || semPricingTestEnabled
  let freeTrial = hasFreeTrial
  let price = planPrice
  let experiment = priceExperiment
  let updatedBasePrice = basePrice
  let discountPercentage = 0
  if (pricingExperimentReady && (pricingTestEnabled || semPricingTestEnabled)) {
    let pricingJson = '{}'
    if (pricingTestEnabled) {
      pricingJson = pricingVariables.pricingJson as string
    } else if (semPricingTestEnabled) {
      pricingJson = semPricingVariables.pricingJson as string
    }
    const data = safeParseJson(
      pricingJson,
      hasFreeTrial,
      planPrice,
      priceExperiment,
      basePrice
    )
    freeTrial = data.freeTrial
    price = data.price
    experiment = data.pricingExperiment
    updatedBasePrice = data.basePrice
    const offeredPrice = Number(price.substring(1))
    // Double Bitwise NOT is essentially the same as Math.floor for positive
    // numbers, but only fast
    // eslint-disable-next-line no-bitwise
    discountPercentage = ~~(((updatedBasePrice - offeredPrice) / updatedBasePrice) * 100)
  }
  return {
    freeTrial,
    price,
    experiment,
    pricingExperimentReady,
    pricingExperimentEnabled,
    basePrice: updatedBasePrice,
    discountPercentage,
  }
}

export default usePriceTest
